const en = {
  login: {
    title: 'Welcome',
    email: 'Email',
    intro: 'Login to your account',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    forgotPassword: 'Forgot password?',
    submit: 'Sign in',
    noAccount: "Don't have an account?",
    signUp: 'Sign up',
    success: 'You have been successfully logged in.',
    error: 'There was an error logging you in.',
    missingCredentials: 'Please enter a valid email and password.',
    blocked: 'Your account has been temporarily shut down.',
  },
  changePassword: {
    title: 'Change Password',
    intro:
      'Please confirm your account email, and a one time code will be sent to reset your password.',
    email: 'Email',
    login: 'Log in',
    oneTimeCode: 'One Time Code',
    confirmPassword: 'Confirm Password',
    newPassword: 'New Password',
    minPassword: 'Password must be at least 6 characters',
    enterOTP: 'Please enter the OTP sent to your email.',
    verifyOTP: 'Verify OTP',
    sentOTP: 'OTP sent to your email',
    errorSendingOTP: 'Could not send the one-time code email. Please try again or contact support.',
    invalidOTP: 'Invalid OTP',
    verifiedOTP: 'OTP verified successfully',
    passwordChanged: 'Password changed successfully',
    errorChangingPassword: 'Error changing password',
    buttonText: 'Change password',
    errorNoUser: 'There is no user associated with the email {email}.',
  },
  logout: {
    success: 'You have been successfully logged out.',
    error: 'There was an error logging you out.',
  },
  resetPassword: {
    title: 'Reset Password',
    email: 'Email',
    submit: 'Send reset link',
    backToLogin: 'Back to login',
    success: 'Password reset email sent',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    changePassword: 'Change Password',
  },
  validation: {
    required: 'This field is required',
    invalidEmail: 'Invalid email address',
    passwordMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters',
  },
  fields: {
    fullName: 'Full Name',
    email: 'Email',
    phone: 'Phone',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    doctor: 'Doctor',
    indications: 'Indications',
    notes: 'Notes',
    id: 'ID',
    patientId: 'Patient ID',
    dateOfBirth: 'Date of Birth',
    gender: 'Gender',
    created: 'Date Added',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    createdBy: 'Created By',
    male: 'Male',
    female: 'Female',
    other: 'Other',
    fileSize: 'File Size',
    revisionHistory: 'Revision history',
    exportStatus: 'Export Status',
  },
  patientsPage: {
    title: 'Patients',
    description: 'Manage your patients',
    newPatient: 'New Patient',
    instructions: `##### **Who are you adding?**
Fill in the fields to create a patient profile.
##### **Tips:**
- indicates a required field
- Filling in the optional field might be handy for later
- All patient data is anonymously stored`,
  },
  addPatientPage: {
    title: 'Add Patient',
    description: 'Add a new patient to the system',
    success: 'Patient added successfully',
    error: 'Error adding patient',
    submitText: 'Add Patient',
  },
  sections: {
    generalPatientInformation: 'General Patient Information',
    medicalInformation: 'Medical Information',
    orderInformation: 'Order Information',
  },
  placeholders: {
    fullName: 'Enter full name',
    email: 'Enter email address',
    phone: 'Enter phone number',
    doctor: 'Enter doctor information',
    indications: 'Enter indications information',
    notes: 'Enter notes',
    patientId: 'Enter patient ID',
    dateOfBirth: 'Enter date of birth',
    gender: 'Enter gender',
    password: 'Enter password',
    confirmPassword: 'Confirm your password',
    companyName: 'Enter company name',
    clientId: 'Enter client ID',
    line1: 'Enter address line 1',
    city: 'Enter city',
    postalCode: 'Enter postal code',
    country: 'Enter country',
    state: 'Enter state',
  },
  onUpdate: {
    title: 'Update Patient',
    success: 'Patient updated successfully',
    error: 'Error updating patient',
  },
  onUpdateOrder: {
    title: 'Update Order',
    success: 'Order updated successfully',
    error: 'Error updating order',
  },
  onDelete: {
    title: 'Delete Patient',
    message:
      'Are you sure you want to delete this patient? All 3D-scans, E-limbs and unfinished products linked to this patient will be deleted. Ordered products will be kept in the order page, but cannot be changed.',
    confirmText: 'Delete',
    success: 'Patient deleted successfully',
    error: 'Error deleting patient',
    elimb: {
      title: 'Delete E-limb',
      message: 'Are you sure you want to delete this E-limb?',
      success: 'E-limb deleted successfully',
      error: 'Error deleting E-limb',
    },
    scan: {
      title: 'Delete Scan',
      message: 'Are you sure you want to delete this Scan?',
      success: 'Scan deleted successfully',
      error: 'Error deleting scan',
    },
  },
  onDeleteUser: {
    title: 'Delete User',
    message:
      'Are you sure you want to delete this user? All patients, ordered products, 3D-scans and E-limbs linked to this user will transferred to a different user.',
    transferTo: 'Please specify to whom the data will be transferred:',
    confirmText: 'Delete',
    success: 'User deleted successfully',
    error: 'Error deleting user',
    selectUserToTransfer: 'Select user to transfer to',
    noUserToTransfer: 'No user to transfer to',
    lastAdmin: 'There must always be at least one admin. To change this, please contact OTB.',
    forbidden: "You don't have permission to delete this account.",
    referenced:
      'This account is still linked to other records and could not be deleted. Nothing was changed — please contact OTB.',
    invalidTransfer:
      'The data can only be transferred to another active user of the same company. Nothing was changed — please pick a different user.',
  },
  onCreatePatient: {
    existingPatientName: {
      title: 'Existing patient',
      message:
        'You created a patient with the same name as an already existing patient: {fullName}.\nDo you want to save this as a new patient or edit the existing patient?',
      confirmText: 'Save as new patient',
      cancelText: 'Edit existing patient',
    },
    existingPatientId: {
      title: 'Existing patient',
      message:
        'You created a patient with the same ID as an already existing patient: {fullName}.\nDo you want to edit the existing patient or cancel the new patient creation?',
      confirmText: 'Edit existing patient',
      cancelText: 'Cancel patient creation',
      cancelMessage: 'Cancelled patient creation',
    },
  },
  onEditPatient: {
    existingPatientName: {
      title: 'Existing patient',
      message:
        'You updated a patient with the same name of an already existing patient: {fullName}.\nDo you want to apply your changes to the current patient?',
      confirmText: 'Apply changes',
      cancelText: 'Cancel patient update',
    },
  },
  actions: {
    edit: 'Edit',
    delete: 'Delete',
    view: 'View',
    add: 'Add',
    save: 'Save',
    cancel: 'Cancel',
    continue: 'Continue editing',
    reorder: 'Reorder',
    duplicate: 'Duplicate',
    duplicateConfig: 'Duplicate & update configuration',
    addToCart: 'Add to cart',
    removeFromCart: 'Remove from cart',
    upload: 'Upload',
    review: 'Review product',
  },
  messages: {
    created: 'Patient created',
    updated: 'Patient updated',
    deleted: 'Patient deleted',
    createError: 'Error creating patient',
    updateError: 'Error updating patient',
    deleteError: 'Error deleting patient',
  },
  confirmations: {
    update: {
      title: 'Update',
      message: 'Are you sure you want to update this item?',
      confirmText: 'Update',
    },
    delete: {
      title: 'Delete',
      message: 'Are you sure you want to delete this item?',
      confirmText: 'Delete',
    },
    create: {
      confirmText: 'Create',
    },
  },
  overviewPage: {
    title: 'Patient Overview',
    description: 'View and manage patient details',
    newProduct: 'New Product',
    noProducts: 'No products available',
    productId: 'Product ID',
    productType: 'Product Type',
    createdOn: 'Created On',
    status: 'Status',
    newScan: 'New Scan',
    noScans: 'No scans available',
    scanId: 'Scan ID',
    scanName: 'Scan Name',
    scannedLimb: 'Scanned Limb',
    newElimb: 'New E-limb',
    noElimbs: 'No e-limbs available',
    elimbId: 'E-limb ID',
    elimbType: 'E-limb Type',
    editNotes: 'Edit Notes',
    notesUpdated: 'Notes updated successfully',
    notesUpdateError: 'Error updating notes',
    scans: 'Scans',
    elimbs: 'E-limbs',
    products: 'Products',
  },
  admin: {
    title: 'Admin Panel',
  },
  users: {
    title: 'Users',
    add: 'Add User',
    edit: 'Edit User',
    delete: 'Delete User',
    sections: {
      generalInfo: 'General user info',
      loginInfo: 'Login Information',
      roles: 'Roles',
      passwordPicker: {
        title: 'Who needs to set the password?',
        true: 'You',
        false: 'The user',
      },
      warning: {
        alert: 'This user did not set up a password yet.',
        button: 'Send new e-mail',
      },
      platformAccess: {
        title: 'Platform access',
        field: 'Blocked',
      },
    },
    roles: {
      professional: 'Professional',
      admin: 'Administrator',
      distributor: 'Distributor',
      master: 'Master',
    },
    instructions: `##### **What to do?**
Fill in the (required) fields to create a new company/distributor and assign a user to the company.
##### **Tips:**
- The required fields are indicated with a *
- If you don't fill in a patient ID, the software will automatically generate one. you can change this later`,
    fields: {
      id: 'User ID',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      createdAt: 'Created at',
      orderedProducts: 'Ordered Products',
      unfinishedProducts: 'Unfinished Products',
      roles: 'Roles',
    },
  },
  companies: {
    title: 'Companies',
    add: 'Add Company',
    edit: 'Edit Company',
    delete: 'Delete Company',
    new: 'New Company',
    description: 'Create a new company',
    filterByDistributor: 'Filter by distributor',
    addDistributor: 'Add distributor',
    sections: {
      generalInfo: 'Company General Information',
      settings: 'Settings',
      address: 'Address',
      users: 'Users',
      companyOverview: 'Company Overview',
      productWorkflows: 'Product Workflows',
      supportTickets: 'Support Tickets',
      orderedProducts: 'Ordered Products',
      advancedSettings: 'Advanced Settings',
      productWorkflowStepConfigurationCard: {
        title: 'Toggle {material} product workflows steps',
      },
    },
    fields: {
      name: 'Company name',
      clientId: 'Client ID (at least 4 characters)',
      clientIdHeader: 'Client ID',
      generalEmail: 'General email address',
      generalPhone: 'General phone number',
      fullName: 'Full name',
      deletedUser: 'Deleted User',
      email: 'Email',
      phone: 'Phone',
      line1: 'Address',
      city: 'City & Post Code',
      state: 'State',
      postalCode: 'Postal code',
      country: 'Country',
      createdBy: 'Created by',
      dateCreated: 'Date created',
      id: 'Company ID',
      openSupportTickets: 'Open Support Tickets',
      orderedProducts: 'Ordered Products',
      users: 'Users',
      unfinishedProducts: 'Unfinished Products',
    },
    settings: {
      useOwnPatientId: {
        label: 'Use own patient ID',
        description:
          'By checking this box, you will be able to define your own patient ID, making it a required field. By unchecking this box, the platform will generate patient IDs randomly.',
      },
      sharePatients: {
        label: 'Share patients between users',
        description:
          'By checking this box, all users within this organization can view, edit and create products for all patients. By unchecking this box, only the user who creates a patient can view, edit or create a product for the patient.',
      },
      useShippingInformation: {
        label: 'Use shipping information for affiliated companies',
        description:
          'By checking this box, all products ordered by companies created by this organization will be shipped and linked to this distributor. (Not editable by company admin)',
      },
      adminOrderFollowUp: {
        label: 'Admin gets included in company order follow up.',
        description: 'By checking this box, the admins get a mail when a product is ordered.',
      },
      orderReferenceFollowUp: {
        label: 'Order reference reminders.',
        description:
          'By checking this box, you will get a daily reminder to fill in your order reference if this has not been filled in yet.',
      },
      credits: {
        title: 'Credits',
      },
      onlyAdminsCanBuyCredits: {
        label: 'Only admins can buy credits',
        description:
          'By checking this box, professionals can only request credits and company administrators buy them.',
      },
      pricePerCredit: {
        label: 'Price per credit (EUR)',
        description:
          'What one credit costs this organization in euro. Leave it at 0 to use the price of its distributor, or else of Orthobroker.',
      },
      creditPriceCarpi: {
        label: 'Carpi (credits)',
        description:
          'Credits, not euro, charged for one Carpi STL. Zero uses the value of its distributor, or else of Orthobroker.',
      },
      creditPriceMino: {
        label: 'Mino (credits)',
        description:
          'Credits, not euro, charged for one Mino STL. Zero uses the value of its distributor, or else of Orthobroker.',
      },
      creditPriceVolar: {
        label: 'Volar (credits)',
        description:
          'Credits, not euro, charged for one Volar STL. Zero uses the value of its distributor, or else of Orthobroker.',
      },
      creditPacks: {
        title: 'Credit pack {pack}',
        visible: {
          label: 'Show this pack',
          description: "Offer it on this company's credits page.",
        },
        credits: 'Credits',
        discount: 'Discount (%)',
      },
      notifications: {
        success: 'Updated company settings.',
        error: 'An error occurred while updating the company settings.',
      },
    },
    whitelabel: {
      title: 'Whitelabel',
      logo: {
        label: 'Company logo',
        description:
          "Replaces the OrthoSolid logo for this company's users. PNG, JPG or WebP, up to 2 MB.",
        upload: 'Choose image',
        remove: 'Remove logo',
        removeTitle: 'Remove company logo',
        removeMessage: "Remove this company's logo? Its users will see the OrthoSolid logo again.",
        empty: 'No company logo — users see the OrthoSolid logo.',
      },
      notifications: {
        success: 'Updated the company logo.',
        removed: 'Removed the company logo.',
        error: 'An error occurred while updating the company logo.',
        notAllowed: 'Only master users can change the company logo.',
        noFile: 'Choose an image first.',
        invalidType: 'That file is not a supported image. Use PNG, JPG or WebP.',
        tooLarge: 'That image is too large. The maximum size is 2 MB.',
      },
    },
    actions: {
      create: {
        errorPrefix: 'Error creating company:',
      },
      delete: {
        button: 'Delete company',
        title: 'Delete company',
        message: 'Are you sure you want to delete this company?',
        success: 'Company has been successfully deleted.',
        error: 'An error occurred while deleting the company.',
      },
      edit: {
        success: 'The company was updated successfully.',
        errorPrefix: 'Error updating company:',
      },
      companyID: 'Company ID is required.',
      error: 'Client ID can only contain letters, numbers, dots, hyphens, and underscores.',
      saveDecisionFlowOptions: {
        success: 'The options this company may pick in {flow} were saved.',
        notAllowed:
          'You can only set the options of companies you registered, within your own options.',
        error: 'The options for {flow} could not be saved. Reload the page and try again.',
      },
      toggleProductWorkflowStepConfigurations: {
        field: 'Toggle {material} product workflows steps',
        productionLocation: {
          title: 'Production Location',
        },
        material: {
          title: 'Material',
        },
        thumbSupport: {
          title: 'Thumb Support',
        },
        braceLength: {
          title: 'Brace Length',
        },
        openingLocation: {
          title: 'Opening Location',
        },
        hinges: {
          title: 'Hinges',
        },
        hingedOption: {
          title: 'Hinged Option',
        },
        padding: {
          title: 'Padding',
        },
        extraPadding: {
          title: 'Extra Padding',
        },
        straps: {
          title: 'Straps',
        },
      },
      duplicateConfig: {
        title: 'Duplicate product to a new configuration',
        noOutcome:
          'No configuration matches these answers. Change an answer, or ask an administrator to complete the decision flow.',
      },
    },
    instructions: `##### **What to do?**
Fill in the (required) fields to create a new company/distributor and assign a user to the company.
##### **Tips:**
- The required fields are indicated with a *
- If you don't fill in a patient ID, the software will automatically generate one. you can change this later`,
  },
  roles: {
    title: 'Roles',
    admin: 'Admin',
    distributor: 'Distributor',
    master: 'Master',
    user: 'User',
  },
  status: {
    pending: 'Order Pending',
    inProgress: 'Order In Progress',
    completed: 'Order Completed',
    cancelled: 'Order Cancelled',
  },
  orders: {
    title: 'Orders',
    new: 'New Order',
    details: 'Order Details',
    status: 'Status',
    date: 'Order Date',
    id: 'Order ID',
    placeOrder: 'Place Order',
    sections: {
      generalInfo: 'General order info',
      shippingInfo: 'Shipping info',
    },
    fields: {
      status: 'Status',
      reference: 'Reference',
      clientId: 'Client ID',
      id: 'Order ID',
      created: 'Created',
      totalItems: 'Total Items',
      notes: 'Notes',
      companyName: 'Company',
      material: 'Material',
    },
    email: {
      received:
        'We are excited to let you know that we have received your order and will process it shortly. Below are the details of your order:',
      beingPrepared:
        'Your order is being prepared, and we will notify you once it is on its way. You can follow the progress by checking the order status in the platform.\nIf you have any questions or need further assistance, feel free to reach out to us at orders@orthosolid.com.',
      signature: 'Best regards,\nThe Orthobroker Team\nhttps://platform.orthosolid.com',
    },
  },
  shipping: {
    title: 'Shipping Information',
    name: 'Full Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    postalCode: 'Postal Code',
    country: 'Country',
  },
  preview: {
    demo: 'Demo preview',
    noPreview: 'No preview available',
    noGlbFound: 'No GLB found',
    loading: 'Loading...',
  },
  modelPreview: {
    orientationLabel: 'Orientation',
    autoOrientation: 'Auto orientation',
    autoOrientationTooltip: 'Automatic orientation based on model dimensions',
    xAxisUp: 'X axis up',
    xAxisUpTooltip: 'Rotate to make X axis vertical',
    yAxisUp: 'Y axis up',
    yAxisUpTooltip: 'Default vertical orientation',
    zAxisUp: 'Z axis up',
    zAxisUpTooltip: 'Rotate to make Z axis vertical',
    error: 'There is no model to display yet.',
    unavailableCombination: 'This combination is not available.',
  },
  support: {
    addTicket: 'Add Ticket',
    title: 'Request help',
    description1:
      "Thanks for reaching out, we're happy to help you solving your issue. To make sure we can provide the right support for all users, we'd like to let you know that there is a thorough platform manual you can read when facing an issue.",
    description2:
      "If your issue is not addressed in the manual, please describe in the box below. We'll have a look at it within 24 hours (during work days). You'll receive a notification when we've addressed your ticket",
    manualButton: 'Platform manual',
    issueLabel: 'Describe your issue',
    issuePlaceholder: 'Please describe your issue here...',
    createButton: 'Create ticket',
    typeLabel: 'Involving',
    typePlaceholder: 'Choose a topic',
    platformManual: 'Platform manual',
    platformManualDescription: 'Read through the guidelines or search for a functionality',
    academy: 'Academy',
    academyDescription: 'Become an expert through video tutorials',
    button: 'Mark as resolved',
    notification: {
      resolved: {
        success: 'Marked as resolved',
        error: 'An unexpected error occurred while marking as resolved. Please try again.',
      },
      under_investigation: {
        success: 'Marked as under investigation',
        error:
          'An unexpected error occurred while marking as under investigation. Please try again.',
      },
    },
  },
  buttons: {
    back: 'Back',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    add: 'Add',
    select: 'Select',
    selected: 'Selected',
    discover: 'Discover',
    readMore: 'Read more',
    continue: 'Continue',
    clear: 'Clear',
  },
  errors: {
    notFound: {
      title: '404',
      subtitle: 'Page Not Found',
      description: "The page you're looking for doesn't exist or has been moved.",
      action: 'Back to Home',
    },
    general: {
      title: 'Something went wrong!',
      description: 'An unexpected error occurred. Please try again.',
      action: 'Try Again',
    },
    noAccess: 'You have no access to this page.',
    duplicate: {
      error: 'Something went wrong while duplicating the product.',
    },
  },
  createUser: {
    errors: {
      alreadyExists:
        'This user already has a working account. If the password needs to be reset, contact your admin.',
      passwordLength: 'Password must be at least 6 characters long.',
      error: 'Error creating authentication:',
      noEmail: 'Something went wrong while retrieving your email. Please contact support.',
      JWTExpired: {
        new: 'Your invitation to the OrthoSolid platform has expired. Please contact your admin for a new one.',
        reset:
          'Your invitation to reset your password has expired. Please contact your admin for a new one.',
      },
    },
    subject: 'Set your password for OrthoSolid.',
    mail: `Dear {fullName},
    
    Your admin has created an account for you on the OrthoSolid platform.
    Go to this link to create a password: {passwordURL}

    Kind regards,
    The OrthoSolid team. 
    `,
    html: `
      <p>Dear {fullName},</p>
      <p>
        Your admin has created an account for you on the OrthoSolid platform.
        Go to <a href="{passwordURL}">this link</a> to create a password.
      </p>
      <p>
        Kind regards,<br />
        The OrthoSolid team.
      </p>
    `,
  },
  onResetPassword: {
    message:
      'When pressing continue, an e-mail will be sent to the user. They will be redirected to a page where they can reset their password.',
    success: 'The email has been sent to the user.',
    error: 'Something went wrong while sending the mail to the user.',
    mail: {
      subject: 'Reset your password for the Orthosolid platform.',
      text: `Dear {fullName},
    
    Your admin has requested a password reset for you.
    Go to this link so you can reset it: {passwordURL}

    Kind regards,
    The OrthoSolid team. 
    `,
      html: `
      <p>Dear {fullName},</p>
      <p>
        Your admin has requested a password reset for you.
        Go to <a href="{passwordURL}">this link</a> so you can reset it.
      </p>
      <p>
        Kind regards,<br />
        The OrthoSolid team.
      </p>
    `,
    },
    errorUserWithoutPassword: `
      <p>This user has an account set up, but no password. Please head to <a href="{passwordURL}">this link</a> so it can be set up.</p>
    `,
  },
  onResendNewPasswordMail: {
    title: 'Send a new password mail',
    message:
      'When pressing continue, an e-mail will be sent to the user. They will be redirected to a page where they have a new chance to set their password.',
  },
  ticketDetails: {
    generalInfo: 'General ticket info',
    involving: 'Involving',
    ticketId: 'Ticket ID',
    scanId: 'Scan ID',
    createdOn: 'Created on',
    createdBy: 'Created by',
    lastAdjusted: 'Last adjusted on',
    lastAdjustedBy: 'Last adjusted by',
    delete: 'Delete',
    deleteTitle: 'Delete ticket',
    deleteConfirm: 'Are you sure you want to delete this ticket?',
    deleteSuccess: 'Ticket deleted',
    deleteError: 'Error deleting ticket',
    resolveTicket: 'Resolve ticket',
    ticketResolved: 'Ticket resolved',
  },
  productCatalogue: {
    title: 'Product Catalogue',
    viewAll: 'View all',
  },
  discussion: {
    title: 'Discussion',
    newMessage: 'New Message',
    send: 'Send',
    reply: 'Reply',
  },
  form: {
    save: 'Save changes',
    delete: 'Delete',
    required: 'Required',
    enterValue: 'Enter value',
    selectDate: 'Select date',
    slider: {
      autoAdjust: 'Toggle automatic calculation',
    },
  },
  notes: {
    title: 'Notes',
    edit: 'Edit notes',
    placeholder: 'Enter notes',
    updated: 'Notes updated',
    updateError: 'Error updating notes',
  },
  referenceNumber: {
    title: 'Reference Number',
    placeholder: 'Your order reference number (if applicable)',
  },
  notifications: {
    success: 'Success',
    error: 'Error',
    info: 'Information',
    warning: 'Warning',
    company: {
      created: 'Company created successfully',
      deleted: 'Company deleted successfully',
      deleteError: 'Error deleting company',
      updated: 'Company updated successfully',
      updateError: 'Error updating company',
    },
    order: {
      created: 'Order created successfully',
      createError: 'Failed to create order! Please contact support',
      insufficientCredits: 'Not enough credits for this order. Top up your credits and try again.',
      creditsMisconfigured:
        'This order could not be priced in credits. Please contact OrthoSolid — buying more credits will not resolve it.',
    },
    user: {
      created: 'User created successfully',
      createError: 'Error creating user',
    },
    product: {
      addedToCart: 'Product added to cart',
      removedFromCart: 'Product removed from cart',
      cartError: 'Error updating cart',
      duplicated: 'The product was duplicated correctly.',
      duplicatedConfig: 'The product was duplicated correctly with a new configuration.',
      deleted: 'Product deleted successfully',
      deleteError: 'Error deleting product',
    },
    model: {
      loadError: 'Error loading model',
    },
    message: {
      creditsLow: {
        subject: 'Low credit balance',
        message:
          'Your organization has {balance} credits left, which is not enough for another digital file. Top up to keep ordering.',
      },
      creditsRequested: {
        subject: 'Credits requested',
        message: '{requesterName} asked for {credits} more credits for your organization.',
      },
      orderPlaced: {
        subject: 'Order #{orderId} placed.',
        message: 'Your order has been placed successfully.',
      },
      orderInProcess: {
        subject: 'Order #{orderId} in progress',
        message: 'All products from your order are in manufacturing.',
      },
      orderDelayed: {
        subject: 'Order #{orderId} delayed',
        message: 'Something went wrong with your order. We’ll take up contact if needed.',
      },
      orderShipped: {
        subject: 'Order #{orderId} finished',
        message: 'All products in your order are shipped and will arrive soon.',
      },
      orderInvoiced: {
        subject: 'Order #{orderId} Invoiced',
        message: 'Your order #{orderId} has been invoiced.',
      },
      productUpdated: {
        subject: '{productName} {productId} has been updated by support',
        message:
          'The product "{productName}" (ID: {productId}) has been updated by our support team.',
      },
      supportHasReactedToYourHelpRequest: {
        subject: 'Product Update: {productName} - {productId}',
        message: 'We’ve reacted on your request for help.',
      },
      announcement: {
        title: 'New Announcement',
        description:
          'By pressing "Send notification", an in platform notification will be sent to ALL users.',
        confirmText: 'Send notification',
        success: 'The notification has been sent to all users.',
        error: 'Something went wrong sending the notification to all users.',
        form: {
          subject: {
            title: 'Subject',
            placeholder: 'Please enter a subject...',
          },
          message: {
            title: 'Message',
            placeholder: 'Please type your message...',
          },
          link: {
            title: 'Link',
            placeholder: 'Please enter a link to where you want the user to go...',
          },
        },
      },
      platformUpdates: {
        subject: 'Platform updates',
        message: 'We’ve made some changes to the platform.',
        title: 'New Platform Update',
      },
      newsletter: {
        subject: 'Newsletter',
        message:
          'We have some updates for the platform. Please check the platform for more information.',
      },
    },
    userUpdated: 'User updated',
    errorUpdatingUser: 'Error updating user',
    notificationsCleared: 'Notifications cleared',
    notificationSettingsUpdated: 'Notification settings updated',
    patientDataUpdated: 'Patient data updated successfully',
    patientDataUpdateFailed: 'Patient data update failed',
    fileDownloadedSuccessfully: '3D file downloaded successfully',
    supportEditsAccepted: 'Support edits accepted',
    supportEditsFailed: 'Failed to apply support edits',
    changeRequestSubmitted: 'Change request submitted successfully',
    changeRequestFailed: 'Failed to submit change request',
    mustBeLoggedIn: 'You must be logged in to request changes',
    commentMinLength:
      'Please provide a comment with at least {minLength} characters for your change request',
    delete: {
      title: 'Delete Notification',
      message: 'Are you sure you want to delete this notification?',
      confirmText: 'Delete',
      success: 'Notification deleted successfully',
    },
  },
  navigation: {
    back: 'Back',
    backToList: 'Back to list',
    backToOverview: 'Back to overview',
    breadcrumbs: 'Breadcrumb navigation',
    currentPage: 'Current page',
    menu: {
      dashboard: 'Dashboard',
      patients: 'Patients',
      products: 'Products',
      orders: 'Orders',
      explore: 'Explore',
      support: 'Support',
      admin: 'Admin',
      manufacturing: 'Manufacturing',
      documentation: 'Documentation',
    },
  },
  editor: {
    save: 'Save changes',
    cancel: 'Cancel',
    discard: 'Discard changes',
    confirmDiscard: 'Are you sure you want to discard your changes?',
  },
  menu: {
    notifications: 'Notifications',
    messages: 'Messages',
    cart: 'Cart',
    settings: 'Settings',
    toggleMenu: 'Toggle menu',
    closeMenu: 'Close menu',
    account: 'Account',
    createNew: 'Create new',
    noNotifications: 'No notifications',
    noProducts: 'There are no products in your cart',
    goToCheckout: 'Go to checkout',
    accountSettings: 'Account settings',
    logout: 'Log out',
    newProduct: 'New Product',
    newDistributorUser: 'New Distributor User',
    newMasterUser: 'New Master User',
    order: 'View order',
    reactionFromSupport: 'See changes',
    announcement: 'Discover more',
    platformUpdates: "See what's new",
    continue: 'Continue',
  },
  details: {
    noLabel: 'No label',
    noValue: 'No value',
    viewDetails: 'View details',
    moreInfo: 'More information',
  },
  cards: {
    viewItem: 'View {name}',
    selectItem: 'Select {name}',
    imageAlt: 'Image of {name}',
    showInfo: 'Show information',
    hideInfo: 'Hide information',
    favourite: 'Mark product as favourite',
    companyFavourite: 'Mark product as company favourite',
  },
  translation: {
    translate: 'Translate text',
    translateTo: 'Translate to {language}',
    translating: 'Translating...',
    translationError: 'Error translating text',
    defaultLanguage: 'Default language',
  },
  brand: {
    name: 'OrthoSolid',
    logoAlt: 'OrthoSolid Logo',
  },
  fileUpload: {
    attachFile: 'Attach File',
    dragAndDrop: 'Drag & drop or click to browse',
    browse: 'Supported formats: {formats}',
    maxSize: 'Maximum file size: {size}',
    invalidType: 'Invalid file type',
    uploadError: 'Error uploading file',
    loadError: 'Could not load 3D file preview',
    gltfExternalRefs:
      'GLTF files with external references are not supported. Please use GLB format instead.',
    uploadSuccess: 'File uploaded successfully',
    remove: 'Remove file',
    validating: 'Validating files...',
    filesNotSupported:
      'File(s) not supported: {rejectedNames}. Supported formats: {acceptedFormats}',
    filesSelected: '{count} of {max} files selected',
    uploadInstructions:
      'Your file will be uploaded. After uploading, you can clean and improve the 3D scan using our 3D Cleaner tool in the next step.',
    charactersMinimum: '{count}/{min} characters minimum',
    color: {
      label: 'Color',
      incoming: 'No color (use file color)',
      presets: {
        white: 'White',
        skin: 'Skin',
        blue: 'Blue',
        green: 'Green',
        red: 'Red',
        black: 'Black',
      },
    },
  },
  supportTicket: {
    discussion: 'Discussion',
    typeMessage: 'Type a message...',
    send: 'Send',
    attachFile: 'Attach File',
    noComments: 'No comments yet',
    addComment: 'Add a comment',
    uploadFile: 'Upload file',
    close: 'Close',
    success: 'Support ticket created.',
    error: 'Error creating support ticket.',
    status: {
      received: 'Received',
      under_investigation: 'Under investigation',
      on_hold: 'On hold',
      resolved: 'Resolved',
    },
    productStatus: {
      dataInput: 'Data input',
      designing: 'Designing',
      readyToOrder: 'Ready to order',
      inCart: 'In cart',
      helpRequested: 'Help requested',
      updatedBySupport: 'Updated by support',
      ordered: 'Ordered',
      demo: 'Demo',
      designRequested: 'Design requested',
      orderedWithoutReview: 'Ordered without need of a review',
      cancelled: 'Cancelled',
    },
    orderStatus: {
      orderPlaced: 'Order placed',
      processing: 'Processing',
      shipped: 'Finished',
      invoiced: 'Invoiced',
      delayed: 'Delayed',
      ignore: 'Ignore',
      demo: 'Demo',
      designRequested: 'Design requested',
      orderedWithoutReview: 'Ordered without need of a review',
      readyForReview: 'Ready for review',
      generating3dFile: 'Generating 3D file',
      threeDFileUnderReview: '3D file under review',
      threeDFileAvailable: '3D file available',
      downloaded: 'Downloaded',
    },
    exportStatus: {
      notAvailable: 'N/A',
      generating: 'Generating',
      failed: 'Failed',
      missing: 'Missing',
      emptyFile: 'Empty file',
      valid: 'Valid',
    },
    buttonText: 'Send message with link to model',
  },
  workflow: {
    back: 'Back',
    next: 'Next',
    save: 'Save',
    exit: 'Exit',
    continue: 'Continue',
    cancel: 'Cancel',
    finish: 'Finish',
    instructions: 'Instructions',
    steps: {
      current: 'Current step',
      previous: 'Previous step',
      next: 'Next step',
    },
    navigation: {
      back: 'Go back',
      forward: 'Go forward',
      exit: 'Exit workflow',
    },
  },
  patientData: {
    add: 'Add Patient Data',
    notFinished: 'Not Finished',
    fields: {
      name: 'Name',
    },
    scanOrientation: {
      label: 'Scan Orientation',
      placeholder: 'Select Scan Orientation',
      left: 'Left',
      right: 'Right',
    },
    dataType: {
      label: 'Data Type',
      file: 'File',
      measurement: 'Measurement Input',
      form: 'Form Input',
    },
    upload: {
      description: 'Upload Description',
      action: 'Upload',
      alignScan: 'Align Scan',
      defineSide: 'Define scan side',
      selectFile: 'Please select a file and scan orientation',
      uploadError: 'Error uploading file',
    },
    createdOn: 'Created On',
    notes: 'Notes',
    actions: {
      editScan: 'Edit Scan',
      editMeasurements: 'Edit Measurements',
      editForm: 'Edit Form',
    },
    linkedProducts: 'Linked products',
    newProduct: 'New product',
    noLinkedProducts: 'No linked products found',
    dataInput: 'Data input',
    selectDataInput: 'Select a data input',
    createdBy: 'Created by',
    editTitle: 'Edit patient data',
  },
  dataGrid: {
    bulkActions: {
      credits: {
        button: 'Credits / Product Workflows',
        title: 'Configure credits',
        error: {
          notAllowed: 'Not allowed',
          maxSelected: 'Select at most {maxSelected} companies',
          companyId: 'Company {companyId} could not be read',
          invalid: 'Nothing to save, or too many companies selected',
        },
        appliesTo: 'Changes are saved to these companies:',
        unchanged: 'Unchanged',
        saved: '{section} saved for {count} companies',
        partiallySaved: '{section} could not be saved for: {companies}',
      },
      scheduleExports: 'Schedule exports',
      changeStatus: 'Change status',
      downloadProduction: 'Production files',
      downloadCsv: 'Order details',
      downloadExports: 'Exports',
      addToCart: 'Add to cart',
      delete: 'Delete',
      notifications: {
        statusSuccess: 'Status changed successfully',
        statusError: 'Failed to change status',
        productionSuccess: 'Production files downloaded successfully',
        productionError: 'Error downloading production files',
        exportSuccess: 'Exports downloaded successfully',
        exportError: 'Error downloading exports',
        exportErrorExplanation:
          'No downloadable products found. Only "Produce yourself" products can be downloaded.',
        exportErrorNoProducts: 'No downloadable products found.',
        exportErrorIncomplete:
          '{failed} of {total} files could not be downloaded ({products}). Check your connection and try again — nothing was marked as downloaded.',
        cartSuccess: 'Products added to cart successfully',
        cartError: 'Error adding products to cart',
        deleteSuccess: 'Products deleted successfully',
        deleteError: 'Error deleting products',
        exportsSuccess: 'Exports scheduled successfully',
        exportsError: 'Error scheduling exports',
        exportsGenerating: 'Exports are being generated. Please try again shortly.',
        orderInfoSuccess: 'Order info downloaded successfully',
        orderInfoError: 'Error downloading order info',
      },
    },
    filter: {
      title: 'Filter',
      apply: 'Apply Filters',
      cancel: 'Cancel',
    },
    showOnlyMyPatients: 'Show only my patients',
    showOnlyMyOrders: 'Show only my orders',
    showOnlyMyProducts: 'Show only my products',
    search: 'Search...',
    manageColumns: 'Manage columns',
  },
  help: {
    requestHelp: 'Request help',
    newTicket: 'Create new ticket',
    openTicket: 'Open ticket',
    viewTicket: 'View ticket',
  },
  comments: {
    seeModel: 'See model',
    download: 'Download',
    author: 'By {name}',
    createdAt: 'Created {date}',
    fileDetails: {
      name: 'Name',
      size: 'Size',
      type: 'Type',
    },
    selectedFile: 'Selected file',
    removeFile: 'Remove file',
  },
  footer: {
    exploreMore: 'Explore more products',
    back: 'Back',
    next: 'Next',
    exit: 'Save & Exit',
    contact: 'Contact',
    location: 'Location',
    copyright: '© {year} Orthobroker bv',
    followUs: 'Follow us',
    newsletter: 'Newsletter',
    on: 'On',
    off: 'Off',
    getUpdates: 'Get updates on our latest products and offers.',
    suscribed: 'You are now subscribed to our newsletter',
    unsubscribed: 'You are now unsubscribed from our newsletter',
    stayUpToDate: 'Stay up to date',
    form: {
      email: 'E-mail address',
    },
  },
  visibility: {
    show: 'Show sensitive data',
    hide: 'Hide sensitive data',
  },
  dashboard: {
    startCreation: 'Start creation flow',
    addNewPatient: 'Add new patient',
    addPatientData: 'Add patient scan',
    continueDesigning: 'Continue designing',
    recentOrders: 'Recent orders',
    viewAll: 'View all',
    columns: {
      productName: 'Product name',
      patientName: 'Patient name',
      status: 'Status',
      orderDate: 'Order Date',
      orderId: 'Order ID',
    },
  },
  redirect: {
    title: 'Redirect Required',
    description: 'You need to complete some steps before accessing this page.',
    patient: 'patient selection',
    dataset: 'dataset selection',
    product: 'product selection',
    form: 'form selection',
    goTo: 'Go to {destination}',
  },
  general: {
    back: 'Back',
    exit: 'Save & exit',
    yes: 'Yes',
    no: 'No',
    on: 'On',
    off: 'Off',
  },
  titles: {
    credits: 'My credits',
    patient: 'Assign patient',
    assignPatient: 'Assign patient',
    createPatient: 'Add new patient',
    product: 'Select product',
    dataset: 'Select data',
    selectForm: 'Select Form',
    editForm: 'Edit Form',
    newForm: 'New Form',
    // elimb workflow
    selectElimb: 'Select E-limb',
    elimb: 'Measure E-limb',
    // scan workflow
    selectScan: 'Select scan',
    alignScan: 'Align & clean scan',
    admin: 'Admin',
    catalog: 'Catalog',
    checkout: 'Checkout',
    academy: 'Academy',
    explore: 'Explore',
    manufacturing: 'Manufacturing',
    orders: 'Orders',
    dashboard: 'Dashboard',
    patientData: 'Patient Data',
    products: 'Products',
    productWorkflowCategory: 'Select Category',
    tickets: 'Tickets',
  },
  product: {
    title: 'Product',
    new: 'New Product',
    empty: 'No Products',
    description: 'Product Description',
    overview: {
      title: 'Product Overview',
    },
    code: 'Product Code',
    sections: {
      generalInfo: 'General product info',
      details: 'Product details',
      specifications: 'Product specifications',
    },
    fields: {
      status: 'Status',
      patient: 'Patient',
      type: 'Product type',
      id: 'Product ID',
      createdOn: 'Created on',
      createdBy: 'Created by',
      lastAdjustedOn: 'Last adjusted on',
      lastAdjustedBy: 'Last adjusted by',
      orderReference: 'Order reference',
      linkedScan: 'Linked scan',
      copyFrom: 'Copy from',
      edgePiping: {
        0: 'Off',
        1: 'Everywhere',
        2: 'Hand only',
        3: 'Thumb only',
      },
      thumbWebFoldingEdge: {
        0: 'Off',
        1: 'Low',
        2: 'Medium',
        3: 'High',
      },
      buckleType: {
        0: 'No buckle',
        1: 'FDM Loop',
      },
      dorsal: 'Dorsal',
      palmar: 'Palmar',
      transversalPalmArchIntensity: {
        0: 'None',
        1: 'Low',
        2: 'Medium',
        3: 'High',
      },
      productFinishing: {
        0: 'Printed shell only',
        3: 'Orthosis with custom lining',
        1: 'Orthosis with custom lining and separate strap set',
        2: 'Orthosis with custom lining and attached straps',
      },
    },
    properties: {
      title: 'Product Properties',
      edit: 'Edit Properties',
      hinge_type: 'Type of Hinge',
      brace_thickness: 'Brace Thickness',
      material: 'Material',
      finishing: 'Finishing',
      error: 'Error editing properties',
    },
    actions: {
      delete: {
        title: 'Delete product',
        message: 'Are you sure you want to delete this product?',
        confirmText: 'Delete',
        error:
          'The product {id} cannot be deleted because it is linked to support ticket {supportTicketId}.',
      },
      addToCart: 'Add to cart',
      removeFromCart: 'Remove from cart',
      order: 'Go to checkout',
      showSpecifications: 'Show specifications',
      showInfo: 'Product information',
    },
    next: 'Select data',
    add: 'Add Product',
    workflow: {
      instructions: {
        start: `##### **What to do?**
Select the best suited variant.
##### **Tips:**
- Click on the information icon to get a short description of the product`,
        overview: `##### **{title}**
This is the orthosis you will be creating.
##### **Tips:**
- You can favourite this design by clicking the favourite button.`,
        flowStepTitle: '##### **{number}. {question}**',
        overviewTitle: '##### **{title}**',
        overviewHeading: 'Overview',
        flowStep: `##### **{number}. {question}**
Pick the answer that best suits your patient.
##### **Tips:**
- You can change an earlier answer by clicking its step above.`,
        noOutcome:
          'No product matches these answers. Go back and change an answer, or ask an administrator to complete the decision flow.',
        noFlow:
          'This product family has no active decision flow yet. Ask an administrator to activate it.',
        noOptions: 'No answer is available for this step with the answers given so far.',
        outcomeFailed:
          'The product for these answers could not be loaded. Check your connection and reload the page.',
        outcomeNoWorkflow:
          'This product has no product workflow yet, so it cannot be created. Ask an administrator to set one.',
        error: 'Something went wrong calculating the outcome.',
      },
      thumbDiameter: {
        confirmText: 'Continue',
        error: 'Please enter a valid thumb diameter.',
      },
      extraPadding: {
        error: 'Please enter if you want extra padding or not.',
        chip: {
          yes: 'Extra padding',
          no: 'No extra padding',
        },
      },
      favourite: {
        title: 'Favourite',
        success: 'Marked the product as favourite.',
        error: 'Error favouriting carpi production configuration',
      },
      unFavourite: {
        title: 'Remove from favourites',
        message:
          'Are you sure you want to remove this {product} variation from your list of favourites?',
        confirmText: 'Remove',
        success: 'Unmarked the product as favourite.',
        error: 'Error unmarking the product as favourite.',
      },
      companyFavourite: {
        title: 'Company favourite',
        success: 'Marked the product as favourite for the company.',
        error: 'Error marking the product as favourite for the company.',
      },
      companyUnfavourite: {
        title: 'Unmark the product as favourite for the company.',
        message:
          'Are you sure you want to remove this {product} variation from your list of favourites for the company?',
        confirmText: 'Remove',
        success: 'Unmarked the product as favourite for the company.',
        error: 'Error unmarking the product as favourite for the company.',
      },
      outcome: {
        title: 'Overview',
      },
      duplicate: {
        baskoMultimotion: 'Basko Multimotion',
        caroliDyna: 'Caroli Dyna',
        caroliMono: 'Caroli Mono',
        albrecht: 'Albrecht',
        ultraFlex: 'UltraFlex',
      },
    },
  },
  productWorkflowCategory: {
    instructions: `##### **Which product does your patient need?**
Select a product family.
##### **Tips:**
- Click on the information icon to get a short description of the products
- After you have chosen the product family you can choose which variant you want to create`,
    next: 'Select product',
  },
  patient: {
    info: 'Patient Info',
    instructions: `##### **Who are you creating a product for?**
Either create a new patient or assign one from your patient list.`,
    new: 'Add new patient',
    pick: 'Select from patient list',
    exit: 'Exit',
    next: 'Select product category',
    create: 'Add new patient',
    addPatient: 'Add Patient',
    close: 'Close',
    name: 'Patient Name',
  },
  dataset: {
    elimb: {
      instructions: `##### **What are the measurements of the patient's arm?**
Either create a new E-limb by measuring your patient or select one from you patient's profile.
##### **Tips:**
- Make sure that if you choose an existing E-limb it is still up to date
- When creating a new E-limb, make sure you have following tools nearby: Calipers, ruler`,
      create: 'Create new E-limb',
      select: "Select E-limb from patient's profile",
      empty: 'No E-limbs found',
      next: 'Create E-limb',
      save: 'Define joint angles',
    },
    scan: {
      instructions: `##### **What are the measurements of the patient's arm?**
Either upload a new 3D-scan or select one from your patient's profile.
##### **Tips:**
- Make sure that if you choose an existing 3D-scan it is still up to date
- When you upload a new 3D-scan you will have to align and clean it.
- To create the perfect scan to use in this software, see Scan manual`,
      upload: 'Upload new 3D scan',
      select: "Select 3D scan from patient's profile",
      empty: 'No 3D scans found',
      next: 'Clean Scan',
      save: 'Save cleaned scan',
      custom: {
        next: 'Product description',
      },
    },
    form: {
      assign: 'Assign Form',
      empty: 'No forms available',
      instructions: '##### **Please select a form to continue or create a new one**',
      select: 'Select form',
      new: 'Create new form',
      save: 'Save form',
      next: 'Next step',
      request: 'Request product',
      altNext: 'Order product',
      altButton: 'Update form',
      name: {
        title: 'Give your form a name.',
        placeholder: 'Enter name',
      },
    },
    configure: 'Product creator',
  },
  list: {
    title: 'Products',
    empty: 'No products found',
    fields: {
      productId: 'Product ID',
      productType: 'Product type',
      createdOn: 'Created on',
      status: 'Status',
    },
    actions: {
      new: 'New product',
    },
  },
  model: {
    locked: 'The model is currently locked because it has been ordered or added to the cart.',
  },
  mesh: {
    notFound: {
      title: 'Mesh not found',
      message: 'The mesh was not found in the scene',
    },
    loaded: {
      title: 'Mesh loaded',
      message: 'Mesh loaded successfully',
    },
    align: {
      title: 'Please align scan',
      message: "Press 'Align Scan' before saving scan.\n(Save again to continue without aligning) ",
    },
    clean: {
      title: 'Please clean scan',
      message:
        "Press 'Clean Scan' before saving scan.\n(Save again to continue without cleaning scan) ",
    },
  },
  settings: {
    title: 'Settings',
    sections: {
      company: 'Company Settings',
      user: 'User Settings',
      general: 'General Settings',
    },
    fields: {
      useOwnPatientId: {
        label: 'Use own patient ID',
        description:
          'Checking this box allows you to define your own patient ID, making it a required field. By unchecking this box, the platform will generate patient IDs randomly.',
      },
      sharePatients: {
        label: 'Share patients among users',
        description:
          'By checking this box, all users within this organisation can see, edit and create products for all patients. By unchecking this box, only the user who creates a patient is able to see, edit or create a product for the patient.',
      },
      useShippingInformation: {
        label: 'Use shipping information for child companies',
        description:
          'By checking this box, all products ordered by companies created by this organisation will be shipped and linked to this distributor. (Not editable by the company admin)',
      },
      anonymizedStartUp: {
        label: 'Anonymized start up',
        description: 'Hide sensitive information on startup',
      },
      assistedMode: {
        label: 'Assisted mode',
        description: 'Enable guided assistance throughout the application',
      },
      preferredLanguage: {
        label: 'Preferred language',
        description: 'Select your preferred language for the application',
      },
    },
    myAccount: 'My Account',
    announcement: {
      button: 'Send a new announcement',
    },
    platformUpdate: {
      button: 'Send a platform update',
    },
    notificationSettings: 'Notification settings',
    notifications: {
      inPlatform: 'In platform',
      byEmail: 'By e-mail',
    },
  },
  scan: {
    title: 'Scan info',
    fields: {
      id: 'Scan ID',
      name: 'Scan Name',
      orientation: 'Orientation',
      lastAdjustedOn: 'Last adjusted on',
      lastAdjustedBy: 'Last adjusted by',
    },
  },
  elimb: {
    title: 'Elimb information',
    fields: {
      id: 'Elimb ID',
      name: 'Elimb Name',
      orientation: 'Orientation',
      lastAdjustedOn: 'Last adjusted on',
      lastAdjustedBy: 'Last adjusted by',
    },
  },
  descriptions: {
    credits: 'Buy credits and see what your organization can still print.',
    admin: 'Admin Description',
    catalog: 'Catalog Description',
    checkout: 'Checkout Description',
    academy: 'Academy Description',
    explore: 'Explore Description',
    orders: 'Orders Description',
    products: 'Products Description',
    tickets: 'Tickets Description',
  },
  distributor: {
    create: 'Create Distributor',
    add: 'Add User',
  },
  cart: {
    empty: 'Cart is empty',
    delete: {
      title: 'Remove from Cart',
      message: 'Are you sure you want to remove this item from your cart?',
      confirmText: 'Remove',
      success: 'Item removed from cart successfully',
    },
  },
  checkout: {
    title: 'Checkout',
    contactInformation: {
      title: 'Contact Information',
      description: 'Contact Information Description',
    },
    products: {
      title: 'Products',
      description: 'Products Description',
    },
  },
  academy: {
    title: 'Academy',
  },
  manufacturing: {
    title: 'Manufacturing',
    orders: {
      title: 'Manufacturing - Orders',
      tab: 'Regular Orders',
    },
    designRequested: {
      title: 'Manufacturing - Design Requested',
    },
    ignoredItems: {
      title: 'Manufacturing - Ignored Items',
      tab: 'Ignored Items',
    },
    creditOrders: {
      title: 'Manufacturing - Credit Requests',
      tab: 'Credit Requests',
    },
    noItemsFound: 'No manufacturing items found',
    stlFiles: 'STL Files',
    rows: {
      selectRow: 'Select row',
      unselectRow: 'Unselect row',
      selectAllRows: 'Select all rows',
      unselectAllRows: 'Unselect all rows',
    },
  },
  common: {
    language: 'Language',
    loading: 'Loading...',
    noResults: 'No results found',
    search: 'Search',
    noValue: '-',
    loadingPage: 'Loading page...',
    loadingData: 'Loading data...',
    processing: 'Processing...',
    removeFromCart: 'Remove from cart',
    fields: {
      created: 'Created',
    },
    cancel: 'Cancel',
    hiddenData: 'This data is hidden',
  },
  tickets: {
    fields: {
      product: 'Product',
      status: 'Status',
      createdBy: 'Created By',
      id: 'Ticket ID',
      involving: 'Involving',
      description: 'Description',
      company: 'Company',
    },
  },
  products: {
    fields: {
      status: 'Status',
      name: 'Name',
      patientName: 'Patient Name',
      id: 'Product ID',
      notes: 'Notes',
      producedBy: 'Produced by',
    },
  },
  documentation: {
    title: 'Documentation',
    notAvailable: 'No documentation available at this time.',
    loading: 'Loading content...',
    selectDocument: 'Select a document from the table of contents.',
  },
  explore: {
    title: 'Explore',
  },
  formFields: {
    togglePasswordVisibility: 'toggle password visibility',
  },
  features: {
    currentlyDisabled: 'This feature is currently disabled',
  },
  components: {
    selectPatient: 'Select a patient',
    patientLabel: 'Patient',
    meshInspectorFrame: 'MeshInspector Frame',
    doNotShowAgain: 'Do not show this pop-up next time',
    submitText: 'Start designing',
    supportPreview: {
      title: 'Support preview',
      description: 'You are viewing a support preview. Accept changes to continue',
      accept: 'Accept',
    },
    modelPreviews: {
      viewSelection: 'model view selection',
      productView: 'product view',
      patientDataView: 'patient data view',
      finalProduct: 'Final Product',
      threeDScan: '3D Scan',
    },
    meshInspector: {
      saveWarnings: {
        title: 'Save scan',
        lead: 'Before saving, please note:',
        notCleanedBullet: "The mesh hasn't been cleaned. Run 'Clean Scan' to heal it.",
        notAlignedBullet: "The scan isn't aligned. Run 'Align Scan' to orient it.",
        proceed: 'Save anyway',
      },
    },
    customProducts: {
      noFormDataAvailable: 'No form data available for this product.',
      changeRequestHistory: 'Change Request History',
      anonymous: 'Anonymous',
      uploadModel: 'Upload model',
      uploadModelTitle: 'Upload Model',
      productFormData: 'Product Form Data',
      requestChangesButton: {
        title: 'Request Changes',
        description:
          'This request will change the product status and notify the OrthoSolid team. Our team will review your feedback and provide a new version of the product based on your specifications.',
      },
      buttons: {
        orderTitle: 'Accept and order',
      },
      images: {
        title: 'Attached Images',
        description: 'Upload New Images',
      },
      requestProduct: {
        dialog: {
          title: 'Request custom product',
          message: `By requesting this product, we will create a custom design and in the end you will be invoiced.\n\n**Important:**\n\nNote that it might take up to 3 working days for the design to be created. You will be kept up to date via the platform notifications and e-mail.`,
          confirmText: 'Request product',
        },
      },
      orderProduct: {
        dialog: {
          title: 'Order product',
          message: `By ordering this product, we will create and print a custom design without your verification. The product will be shipped and invoiced.\n\n**Important:**\n\nNote that it might take up to 12 working days for the product to be created. You will be kept up to date via the platform notifications and e-mail.`,
          confirmText: 'Order product',
        },
      },
      updateForm: {
        dialog: {
          title: 'Update form',
          message:
            'By updating the form, this will also be applied to other products that use this form.',
        },
      },
    },
  },
  youtubeEmbed: {
    player: 'YouTube video player',
    allowedFeatures:
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
  },
  configurator: {
    calculating: 'Calculating...',
    error: {
      title: 'Error',
      description: 'Product not found.',
      brace: {
        title: 'A brace needs to be calculated before you can hide it.',
      },
    },
    menu: {
      customTextLimit: 'Enter up to 20 characters. ({length} used)',
      notes: {
        title: 'Information for production',
        productLabel: 'Include patient name on product label',
        placeholder: 'Click to add notes...',
      },
    },
  },
  volarShell: {
    error: {
      noAcces: 'You do not have access to this product.',
      noShell: 'No shell file is available for this product.',
      couldNotGenerate: 'Could not generate a download link for the shell file.',
      failed: 'Failed to fetch the shell file.',
    },
  },
  stlDownload: {
    buttonSingular: {
      title: 'Download 3D file',
    },
    buttonMultiple: {
      title: 'Download 3D files',
    },
    buttonMultipleOrSingular: {
      title: 'Download 3D file(s)',
    },
    buttonGenerating: {
      title: 'Generating 3D file',
    },
    buttonGeneratingMultiple: {
      title: 'Generating 3D file(s)',
    },
    notification: {
      success: '3D files downloaded successfully!',
    },
  },
  demo: {
    form: {
      title: 'Orthosolid Demo',
      email: 'E-mail address',
      name: 'Full name',
      company: 'Company',
      carpi: 'Orthosolid Carpi Demo',
      mino: 'Orthosolid Mino Demo',
      volar: 'Orthosolid Volar Demo',
    },
    reset: 'Reset',
    contactForm: {
      title: 'Contact Form',
      field: 'Text',
      placeholder: 'Enter your message to Orthobroker.',
    },
  },
  marketingWebsite: {
    contact: {
      form: {
        name: 'Name',
        company: 'Company',
        email: 'Email',
        message: 'Message',
        submit: 'Submit',
        value: 'Value',
        success: 'Thank you for contacting OrthoSolid. We will be in contact soon.',
        error:
          'Something went wrong with processing your message. We suggest to directly email us at info@orthobroker.com.',
        recaptchaError: 'Please complete the reCAPTCHA.',
      },
    },
  },
  notificationTypes: {
    creditsLow: 'Low credit balance',
    creditsRequested: 'Credit requests',
    orderPlaced: 'Order placed',
    orderInProcess: 'Order in process',
    orderDelayed: 'Order delayed',
    orderShipped: 'Order finished',
    orderInvoiced: 'Order invoiced',
    reactionsFromSupport: 'Reactions from support',
    platformUpdates: 'Platform updates',
    newsletter: 'Newsletter',
  },
  insights: {
    useCases: 'Use cases',
    guides: 'Guides',
    webinars: 'Webinars',
    comparisons: 'Comparisons',
  },
  website: {
    menu: 'Menu',
  },
  addToCart: {
    errors: {
      noInput: 'Seems like no product was chosen. Please try again with a product.',
      notReadyToOrder: 'This product is not ready to order yet. Please adjust it before ordering.',
      addingToCart:
        'Something went wrong while adding the product to the cart. Please contact support.',
    },
  },
  credits: {
    checkout: {
      title: 'Credits',
      cost: 'This order costs',
      balance: 'Your balance',
      after: 'Balance after this order',
      amount: '{credits} credits',
      amountOne: '1 credit',
      alreadyPaid: 'Already paid',
      unknownCost: 'Price unknown',
      shortfall: 'You need {credits} more credits to place this order.',
      shortfallOne: 'You need 1 more credit to place this order.',
      buy: 'Buy credits',
      requestMore: 'Request more credits',
      unpriced:
        'One or more digital products in this order have no credit price yet. Contact Orthobroker before ordering.',
      covered: 'Your balance covers this order.',
    },
    cart: {
      blocked: 'You do not have enough credits for this product. You need {credits} more credits.',
      blockedOne: 'You do not have enough credits for this product. You need 1 more credit.',
      blockedUnpriced:
        'This product has no credit price yet. Contact Orthobroker before adding it.',
    },
    request: {
      sent: 'Your request was sent to your company administrators.',
      sentShort: 'Request sent',
      noAdmins:
        'Your organization has no administrator to send this request to. Contact Orthobroker.',
      failed: 'We could not send your request. Please try again.',
    },
    available: 'Available credits',
    topUp: 'Top-up',
    requestMore: 'Request more credits',
    requestMoreHint: 'Ask a company administrator to top up your credits.',
    expiry: '{amount} credits expire on {date}',
    expiryOne: '1 credit expires on {date}',
    printsLeft: '{prints} prints left',
    printsLeftOne: '1 print left',
    noPacks: 'No credit packs are available at the moment.',
    loadError: 'Could not load your credits. Please try again.',
    currency: {
      label: 'Change currency:',
      note: 'Prices displayed in currencies other than EUR are indications.',
    },
    orderStatus: {
      requested: 'Requested',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
    },
    table: {
      distributor: 'Distributor',
      credits: 'Credits',
      price: 'Price',
      pack: 'Pack',
      requestedBy: 'Requested by',
      requestedAt: 'Requested on',
      confirmedAt: 'Confirmed on',
    },
    pack: {
      save: 'Save {amount}',
      prints: '{prints} prints',
      printsOne: '1 print',
      add: 'Add',
      addHint: 'Request this pack. Orthobroker will invoice you and add the credits.',
      expiry: 'Credits bought today will expire on {date}',
      requested: 'Requested',
      requestSuccessTitle: 'Request sent',
      requestSuccess:
        'We received your request for {credits} credits. Orthobroker will invoice you, and the credits are added once the payment is confirmed.',
      requestNotAllowed: 'Your company administrator has to request credits for your organization.',
      requestUnavailable: 'This pack is not available for your company right now.',
      rateUnavailable: 'Shown in euro: the {currency} rate is unavailable right now.',
      requestFailed: 'We could not send your request. Please try again.',
    },
    categories: {
      carpi: 'Carpi',
      mino: 'Mino',
      volar: 'Volar',
    },
    faq: {
      title: 'About credits',
      q1: 'What are credits?',
      a1: 'Credits can be used to buy digital files that you can use for 3D-printing in-house.',
      q2: 'Are products produced by Orthobroker included in the credit system?',
      a2: 'No. Products manufactured and delivered by Orthobroker are invoiced separately.',
      q3: 'Can credits be shared within my organization?',
      a3: 'Yes. Credits are shared across your entire organization and can be used by all professionals.',
      q4: 'Do credits expire?',
      a4: 'Yes. All credits expire {months} months after the date of purchase.',
      q5: 'How many credits does a digital file cost?',
      a5: 'The amount of credits required depends on the specific product and will be displayed before purchase.',
    },
  },
  decisionFlows: {
    title: 'Product decision flows',
    description:
      'Build the questionnaire that picks a product and its starting parameters, without code.',
    loading: 'Loading flows',
    saving: 'Saving…',
    flow: 'Flow',
    untitled: 'Untitled',
    reorder: 'Reorder {name}: drag it, use the arrow keys, or open it to move it up or down',
    moveUp: 'Move up',
    moveDown: 'Move down',
    tabs: {
      questions: 'Questions',
      outcomes: 'Outcomes',
      credits: 'Credit system',
      simulator: 'Try it',
    },
    credits: {
      intro:
        "Decide when a product made with this flow consumes credits from the customer's account. The rule is checked when the product is ordered.",
      match: 'Consume credits when',
      matches: {
        all: 'All conditions match (AND)',
        any: 'Any condition matches (OR)',
      },
      legend: 'Conditions',
      noQuestions:
        'No question saves its answer to the product configuration, so none can decide credits.',
      unpriced:
        'This category has no credit price, so checkout refuses any product the rule selects.',
      fallback:
        'No conditions: the built-in rule applies, and only products the customer produces themselves consume credits.',
    },
    toolbar: {
      importJson: 'Import JSON',
      exportJson: 'Export JSON',
      imported: 'Imported {title}',
      exported: 'Exported {file}',
      fileTooLarge: 'The file is larger than {size}.',
      hideList: 'Hide the list',
      showList: 'Show the list',
    },
    flows: {
      none: 'There are no flows yet. Create the first one to start.',
      create: 'New flow',
      createTitle: 'New flow',
      settingsTitle: 'Flow settings',
      name: 'Flow name',
      category: 'Product category',
      categoryHint: 'Decides which products this flow can choose from.',
      noCategory: 'No category',
      saveSettings: 'Save settings',
      duplicateTitle: 'Duplicate flow',
      duplicate: 'Create copy',
      copyName: '{title} (copy)',
      live: 'Use in product creation',
      liveHint:
        'Creating a product in this category follows this flow. Switching it on switches off any other flow of the category.',
      liveNeedsCategory:
        'Choose a product category first: the flow guides product creation in that category.',
      liveWithIssues:
        'This flow has {count} issues. Product creation stops wherever they leave a gap.',
      liveTag: 'in product creation',
      cancel: 'Cancel',
      delete: 'Delete flow',
      more: 'More flow actions',
    },
    deleteFlow: {
      title: 'Delete this flow?',
      body: '{title} will be removed with its questions and outcomes. This cannot be undone.',
      confirm: 'Delete flow',
    },
    confirmDelete: {
      confirm: 'Delete',
      cancel: 'Cancel',
      title: 'Delete {name}?',
      conditionTitle: 'Delete the condition on {question}?',
      combinationTitle: 'Delete combination {number}?',
      question:
        'Its answers go with it, and the conditions and combinations that read it forget it. This cannot be undone.',
      answer:
        'The conditions and combinations that pick this answer forget it. This cannot be undone.',
      condition: 'Without it, this is no longer limited by that question. This cannot be undone.',
      parameter: 'This value is no longer set. This cannot be undone.',
      dialog: 'The dialog and its choices are removed. This cannot be undone.',
      choice: 'The choice and its parameters are removed. This cannot be undone.',
      combination:
        'These answers no longer pick its product. The product configuration itself is kept. This cannot be undone.',
    },
    stepTypes: {
      decisionQuestion: 'Question',
    },
    fields: {
      target: 'Applies to',
      targets: {
        shapediver: '3D model',
        product: 'Product',
      },
      numberRequired: 'Enter a number',
      translate: 'Translate from English',
      translations: 'Translations ({filled}/{total})',
      translateFailed: 'Nothing was translated. Try again in a moment.',
      linked: 'Shared with the product wizard: a change here shows there too',
      linkedNameKept: 'A name is required — the saved one stays until you type a new one',
      translated: 'Translated into {count} languages',
      markdown: {
        bold: 'Bold',
        italic: 'Italic',
        heading: 'Heading',
        bulletList: 'Bulleted list',
        orderedList: 'Numbered list',
      },
      pick: 'Pick a field…',
      unknown: '{name} (not a 3D model parameter)',
      unknownError:
        '{name} is not a parameter of the 3D models of the {category} products this flow can build. Pick one from the list.',
      modelHint:
        'From the 3D models of the {category} products this flow can build. A name not every product has shows which ones do.',
      modelEmpty:
        "{category}'s products have no synced ShapeDiver session yet, so there are no parameters to pick.",
      misspelled: 'Spelled differently in other models',
      spellingWarning:
        'Other 3D models spell this parameter {spellings}. A value reaches only the models spelled exactly like this one: rename the parameter in ShapeDiver so all models match.',
      noCategory:
        'Give this flow a category first: the 3D models of its products are what you can pick from.',
    },
    questions: {
      empty: 'This flow has no questions yet. Add the first one below.',
      count: 'Questions: {count}',
      addQuestion: 'Add question',
      questionTitle: 'Question',
      instructionsTitle: 'Green card title',
      instructionsTitleHint:
        'Heading of the green card in the product wizard. Left empty, the question shows.',
      instructions: 'Green card text',
      instructionsHint: 'Markdown shown under the question in the product wizard',
      answerField: 'Configurator parameter',
      answerFieldHint:
        'Each answer gives this 3D model parameter its value. Left empty, the question sets nothing in the configurator.',
      disabled: 'Disabled',
      disabledDescription: 'Applied in the dialog to duplicate a product to another configuration',
      companyHidable: 'Hidable per company',
      companyHidableDescription:
        'Listed on a company’s Product Workflows tab, where its answers can be hidden for that company',
      decidedValue: 'Value of {field}',
      setDecidedValue: 'Set {field}',
      clearDecidedValue: 'Clear the value of {field}',
      answerCount: '{count} answers',
      options: 'Answers',
      optionLabel: 'Answer',
      addOption: 'Add answer',
      olderParameters: 'Also sets {values}, from before answers lost their parameter list.',
      removeOlderParameters: 'Remove them',
      removeOption: 'Remove answer {name}',
      deleteStep: 'Delete {name}',
      parameterName: 'Saves into',
      min: 'Minimum',
      max: 'Maximum',
      step: 'Step size',
      unit: 'Unit',
      defaultValue: 'Default value',
    },
    conditions: {
      stepLegend: 'Ask only if',
      optionLegend: 'Show only if',
      question: 'Question',
      answers: 'Answers',
      add: 'Add condition',
      remove: 'Remove the condition on {question}',
      pickAnswers: 'Pick at least one answer',
    },
    dialogs: {
      add: 'Add dialog',
      count: 'Dialogs',
      dialogTitle: 'Dialog title',
      description: 'Description',
      control: 'Control',
      controls: {
        single: 'One choice',
        multi: 'Several choices',
        number: 'Number',
        text: 'Text',
      },
      optional: 'Optional',
      showOnOutcome: 'Show on the outcome card',
      choiceLabel: 'Choice',
      addChoice: 'Add choice',
      skip: 'Skip',
      removeChoice: 'Remove choice {name}',
      remove: 'Remove dialog {name}',
      value: 'Value',
    },
    outcomes: {
      addRow: 'Add combination',
      overviewTitle: 'Overview title',
      overviewTitleHint:
        "Heading of the green card on the wizard's last step. Left empty, Overview shows.",
      overview: 'Overview text',
      overviewHint:
        "Markdown on the green card of the wizard's last step, Overview. Left empty, the default text shows.",
      addMissing: 'Add the missing combinations',
      missingTitle: 'Missing combinations: {count}',
      addMissingRow: 'Add combination',
      addMissingRowFor: 'Add combination for {answers}',
      missingMore: '{count} more are not listed. "Add the missing combinations" adds them all.',
      anyAnswer: 'Any answers',
      rowCount: 'Combinations: {count}',
      hint: 'Pick every answer a combination covers. Answers its other cells rule out are greyed out, and a question it never asks stays empty.',
      table: 'Outcomes table',
      actions: 'Actions',
      any: 'Any',
      choose: 'Choose…',
      notAsked: 'Not asked',
      name: 'Name',
      image: 'Image',
      answerCell: '{question}, combination {number}',
      openDetails: 'Details of combination {number}',
      deleteRow: 'Delete combination {number}',
      details: 'Combination {number}',
      productTemplate: 'Product workflow',
      productHint:
        'Saving the combination creates a product configuration of its own that holds the name, workflow, image and answers.',
      linkedHint:
        'Changes apply when you save the combination. A product another flow or category uses gets its own copy.',
      discard: 'Discard',
      saveRow: 'Save combination {number}',
      saved: 'Combination {number} saved',
      close: 'Close',
      product: 'Product',
      imageUpload: 'Upload image',
      imageReplace: 'Replace image',
      imageUploading: 'Uploading…',
      imagePending: 'Saved with the combination',
      imageNeedsSave: 'Save the combination first: the image belongs to the product it creates.',
      imageFailed: 'The image could not be uploaded. Please try again.',
      imageBrowse: 'Browse image in gallery',
    },
    parameters: {
      title: 'Parameters',
      name: 'Field',
      remove: 'Remove parameter {name}',
      valueType: 'Value type',
      reservedName: 'This name is reserved, please choose another.',
      valueTypes: {
        decisionParameterNumber: 'Number',
        decisionParameterBoolean: 'Yes / no',
        decisionParameterString: 'Text',
      },
      value: 'Value',
      add: 'Add parameter',
      help: 'Values this sets once it applies, each for a 3D model parameter or a product field. Where a value goes and its type follow the field.',
      pickOption: 'Pick an option…',
      unsynced: 'Sync this parameter’s ShapeDiver session to pick from its options.',
      true: 'True',
      false: 'False',
    },
    simulator: {
      title: 'Try the questionnaire',
      restart: 'Start over',
      change: 'Change',
      changeAnswer: 'Change answer to {question}',
      valueHint: 'Between {min} and {max}',
      outOfRange: 'Enter a value between {min} and {max}',
      result: 'Selected product',
      shapediverParameters: '3D model settings',
      productParameters: 'Product parameters',
      noParameters: 'None',
      invalid: 'The questionnaire cannot continue:',
    },
    issues: {
      none: 'No issues found',
      count: 'Issues: {count}',
      emptyQuestion: 'This question has no answers.',
      emptyFlow: 'The flow has no steps.',
      unknownCondition: 'A condition reads a question that is not in the flow.',
      forwardCondition: 'A condition reads a question that is asked later.',
      resultWithoutProduct: 'This combination has no product.',
      unknownAnswer: 'A condition or a combination points at an answer that no longer exists.',
      unreachableResult:
        'A more specific combination always wins over this one, so it is never used.',
      conflictingResults: 'Another combination is just as specific for the same answers.',
      impossibleResult:
        'These answers can never be given together, so this combination is never used.',
      unansweredCell: 'Pick the answers this combination covers for every question it asks.',
      impossibleAnswer:
        'An answer picked here can never come with the other answers of this combination.',
      duplicateResult: 'Another combination has exactly the same answers.',
      missingResult: 'Some combinations of answers lead to no product.',
      tooManyCombinations: 'This flow has too many answer combinations to be checked completely.',
      emptyDialog: 'This dialog asks nothing: it has no choices, or no parameter to fill.',
      invalidDialogBounds: 'The minimum is above the maximum, or the step is not above zero.',
      undeclaredAnswerField:
        'This question sets a configurator parameter that the 3D models this flow can build do not have.',
      unknownProductField:
        'This answer sets a product value that is not a product field, so new products drop it. Send it to the 3D model instead.',
      invalidCreditCondition:
        'A credit condition has no answers, or reads a question whose answer is not stored on the product, so orders ignore it.',
    },
    parseErrors: {
      invalidJson: 'The file is not valid JSON.',
      notAFlow: 'The file is not a product decision flow.',
      invalidStep: 'A step is not valid ({path}).',
      invalidOption: 'An answer is not valid ({path}).',
      invalidParameter: 'A parameter is not valid ({path}).',
      invalidResult: 'A combination is not valid ({path}).',
      invalidCondition: 'A condition is not valid ({path}).',
      invalidDialog: 'A dialog is not valid ({path}).',
      invalidAnswerField: 'A question field is not valid ({path}).',
      duplicateKey: 'Two items share the same key ({path}).',
      unknownField: 'The file has a field the flow format does not know ({path}).',
      tooLarge: 'The flow has too many items to open ({path}).',
    },
    storage: {
      'not-allowed':
        'Sanity refused this action. Check that you are signed in and that your role allows it.',
      otherDataset: 'Decision flows are edited in the {dataset} workspace only.',
      openDataset: 'Open the {dataset} editor',
      linkedFailed: 'A shared text could not be saved. Reload this tab to see the stored version.',
      'not-found': 'That flow no longer exists.',
      dropped: 'Flows that could not be read were left out: {count}.',
      invalid: 'The changes could not be saved because the flow is not valid.',
      failed: 'The changes could not be saved. Please try again.',
    },
  },
};

export type EnglishDictionary = typeof en;

export default en;
